// Keeps the gift from Theme settings > Free gift in sync with the cart: adds it once the cart reaches the
// minimum order value and removes it when the cart drops below it. The automatic discount that makes the
// gift free is set up in the Shopify admin, so a gift line that isn't free is removed as well.
if (!customElements.get('free-gift')) {
  const GIFT_PROPERTY = '_free_gift';
  const STORAGE_KEY = 'free-gift';
  const MAX_STEPS = 5;

  customElements.define(
    'free-gift',
    class FreeGift extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        const { variantId, threshold, cart } = JSON.parse(this.querySelector('script').textContent);
        this.variantId = variantId;
        this.threshold = threshold;
        this.hadGift = this.getGiftLines(cart).length > 0;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.sync());

        if (cart.items.length === 0) this.forget();
        if (this.getNextStep(cart)) this.sync();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      sync() {
        if (this.syncing) {
          this.syncQueued = true;
          return;
        }

        this.syncing = true;
        this.reconcile()
          .catch((error) => console.error(error))
          .finally(() => {
            this.syncing = false;
            if (!this.syncQueued) return;
            this.syncQueued = false;
            this.sync();
          });
      }

      async reconcile() {
        let cart = await this.fetchCart();
        if (cart.items.length === 0) this.forget();

        // The gift disappeared without us removing it, so the customer removed it: don't add it back.
        if (this.hadGift && this.getGiftLines(cart).length === 0 && this.qualifies(this.getSubtotal(cart))) {
          this.remember({ declined: true });
        }

        let sections;
        for (let i = 0; i < MAX_STEPS; i++) {
          const step = this.getNextStep(cart);
          if (!step) break;

          if (step.add) {
            ({ sections } = await this.addGift());
            cart = await this.fetchCart();
          } else {
            if (step.notFreeAt) this.remember({ notFree: { currency: cart.currency, subtotal: step.notFreeAt } });
            cart = await this.post(routes.cart_change_url, { id: step.key, quantity: step.quantity });
            sections = cart.sections;
          }
        }

        this.hadGift = this.getGiftLines(cart).length > 0;
        // Not published as a cartUpdate: Dawn's cart components would re-render a second time and drop focus.
        if (sections) this.renderSections(sections, cart);
      }

      // Returns the next cart change that brings the gift in line with the cart, or null when nothing is left to do.
      getNextStep(cart) {
        const subtotal = this.getSubtotal(cart);
        const qualifies = this.qualifies(subtotal);
        const giftLines = this.getGiftLines(cart);
        const giftLine = qualifies ? giftLines.find((line) => line.variant_id === this.variantId) : undefined;
        const unwantedLine = giftLines.find((line) => line !== giftLine);

        if (unwantedLine) return { key: unwantedLine.key, quantity: 0 };
        if (giftLine && giftLine.quantity > 1) return { key: giftLine.key, quantity: 1 };
        if (giftLine && giftLine.final_line_price > 0) return { key: giftLine.key, quantity: 0, notFreeAt: subtotal };
        if (!giftLine && qualifies && this.mayAdd(cart.currency, subtotal)) return { add: true };
        return null;
      }

      // Items at the price the customer pays for them, leaving out the gift itself.
      getSubtotal(cart) {
        return cart.items
          .filter((item) => !this.isGiftLine(item))
          .reduce((subtotal, item) => subtotal + item.final_line_price, 0);
      }

      // The threshold is in the store currency; Shopify.currency.rate converts it to the cart currency.
      qualifies(subtotal) {
        const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
        return Boolean(this.variantId) && subtotal > 0 && subtotal >= this.threshold * rate;
      }

      mayAdd(currency, subtotal) {
        const { declined, notFree } = this.getMemory();
        if (this.addFailed || declined) return false;
        // Once the gift turned out not to be free, only try again when the cart has grown.
        return !notFree || notFree.currency !== currency || subtotal > notFree.subtotal;
      }

      getGiftLines(cart) {
        return cart.items.filter((item) => this.isGiftLine(item));
      }

      isGiftLine(item) {
        return Boolean(item.properties && item.properties[GIFT_PROPERTY]);
      }

      async addGift() {
        try {
          return await this.post(
            routes.cart_add_url,
            { items: [{ id: this.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }] },
            'javascript'
          );
        } catch (error) {
          // Most likely sold out: stop trying for the rest of this page view.
          this.addFailed = true;
          throw error;
        }
      }

      async fetchCart() {
        const response = await fetch(`${routes.cart_url}.js`);
        return response.json();
      }

      async post(url, body, type) {
        const response = await fetch(url, {
          ...fetchConfig(type),
          body: JSON.stringify({
            ...body,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const json = await response.json();
        if (!response.ok) throw new Error(json.description || json.message || response.statusText);
        return json;
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'main-cart-items',
            section: document.getElementById('main-cart-items')?.dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'main-cart-footer',
            section: document.getElementById('main-cart-footer')?.dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ].filter((section) => section.section && document.getElementById(section.id));
      }

      renderSections(sections, cart) {
        const focusedId = document.activeElement?.id;

        this.getSectionsToRender().forEach(({ id, section, selector }) => {
          if (!sections[section]) return;
          const source = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);
          const container = document.getElementById(id);
          if (source) (container.querySelector(selector) || container).innerHTML = source.innerHTML;
        });

        document
          .querySelectorAll('cart-drawer, cart-items, #main-cart-footer')
          .forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));

        this.restoreFocus(focusedId);
      }

      // Re-rendering drops focus from anything inside the cart, so put it back the way Dawn's cart does.
      restoreFocus(focusedId) {
        if (document.activeElement !== document.body) return;

        const previous = focusedId && document.getElementById(focusedId);
        const drawer = document.querySelector('cart-drawer.active');
        if (drawer) {
          const empty = drawer.querySelector('.drawer__inner-empty');
          trapFocus(empty || drawer, previous || drawer.querySelector(empty ? 'a' : '.cart-item__name'));
        } else if (previous) {
          previous.focus();
        }
      }

      getMemory() {
        try {
          return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        } catch (error) {
          return {};
        }
      }

      remember(values) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.getMemory(), ...values }));
        } catch (error) {}
      }

      forget() {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (error) {}
      }
    }
  );
}
